import { Injectable } from "@angular/core";
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest,
    HttpErrorResponse
} from "@angular/common/http";
import { Observable, EMPTY, from } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { SessionHelperService } from "../util-service/sessionhelper.service";
import { tblUserMaster } from "app/models/user/tblusermaster";
import Swal from "sweetalert2";
import { MyspinnerService } from "../util-service/myspinner.service";
import { AuthService } from "./auth.service";

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
    private static isLoggingOut = false;

    constructor(
        private authService: AuthService,
        private router: Router,
        private toastr: ToastrService,
        private mySpinner: MyspinnerService
    ) { }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.headers.get("No-Auth") === "True") {
            const noAuthReq = req.clone({ setHeaders: { 'accept': '*/*' } });
            return next.handle(noAuthReq);
        }

        const tokenData = SessionHelperService.readSessionVariable("userToken");
        const authToken = tokenData ? JSON.parse(tokenData) : null;
        const accessToken = authToken?.token || authToken?.Token;

        if (!accessToken) {
            return EMPTY;
        }

        const roleId = authToken.currentRoleId ?? authToken.roleId ?? authToken.RoleId ?? '0';
        const headers: Record<string, string> = {
            Platform: '1',
            CurrentRoleId: roleId.toString(),
            Authorization: `Bearer ${accessToken}`,
            'accept': '*/*'
        };

        const loginUserRaw = SessionHelperService.readSessionVariable("loginUser");
        if (loginUserRaw) {
            try {
                const loginUser = JSON.parse(loginUserRaw) as tblUserMaster;
                const displayName = [loginUser.firstName, loginUser.lastName]
                    .filter(Boolean)
                    .join(' ')
                    .trim();
                if (displayName) {
                    headers.UserDisplayName = displayName;
                }
            } catch {
                // Ignore malformed session data; request proceeds without display name header.
            }
        }

        const authReq = req.clone({ setHeaders: headers });

        return next.handle(authReq).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    const isProfilePhotoRequest =
                        req.url.includes('/User/RemovePhoto/') ||
                        req.url.includes('/User/UploadProfilePhoto/');

                    if (isProfilePhotoRequest) {
                        this.toastr.error('Unable to update profile photo right now.');
                        return EMPTY;
                    }

                    let failReason = "Session expired. Logging out.";
                    if (error.error) {
                        if (typeof error.error === 'object') {
                            failReason = error.error.Message || error.error.message || failReason;
                        } else if (typeof error.error === 'string') {
                            try {
                                const parsed = JSON.parse(error.error);
                                failReason = parsed.Message || parsed.message || failReason;
                            } catch {
                                if (error.error.trim().startsWith('{')) {
                                    // looks like malformed JSON, ignore
                                } else {
                                    failReason = error.error;
                                }
                            }
                        }
                    }

                    if (failReason && (failReason.includes("Auth Token Not Matched") || failReason.includes("You have been logged out"))) {
                        failReason = "Your session has ended. Please log in again.";
                    }

                    if (AuthInterceptor.isLoggingOut) {
                        return EMPTY;
                    }
                    AuthInterceptor.isLoggingOut = true;
                    this.toastr.error(failReason);
                    this.authService.logout();
                    setTimeout(() => { AuthInterceptor.isLoggingOut = false; }, 1500);
                    return EMPTY;
                }
                throw error;
            })
        );
    }

    // Optional: Enable this method if you support refresh tokens
    /*
    private handle401Error(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
      const tokenData = SessionHelperService.readSessionVariable("userToken");
      const authToken = tokenData ? JSON.parse(tokenData) : null;
  
      if (authToken?.refresh_token) {
        return this.authService.getRefreshToken(authToken.refresh_token).pipe(
          switchMap((newToken) => {
            if (newToken) {
              SessionHelperService.saveSessionVariable("userToken", JSON.stringify(newToken));
              const clonedReq = req.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken.access_token}`
                }
              });
              return next.handle(clonedReq);
            } else {
              return this.handleSessionExpiryWithSweetAlert();
            }
          }),
          catchError(() => this.handleSessionExpiryWithSweetAlert())
        );
      }
  
      return this.handleSessionExpiryWithSweetAlert();
    }
    */

    private handleSessionExpiryWithSweetAlert(): Observable<never> {
        this.mySpinner.hideSpinner();

        return from(
            Swal.fire({
                title: 'Session Expired',
                text: 'Your session has expired.',
                icon: 'warning',
                confirmButtonText: 'Logout',
                allowOutsideClick: false
            })
        ).pipe(
            switchMap(result => {
                if (result.isConfirmed) {
                    this.authService.logout();
                }
                return EMPTY;
            })
        );
    }
}
